import { decodeMessage, encodeMessage, type ControlMessage, type NetworkMessage, type NetworkPlayerState } from "./protocol";
import { createSignalingClient, type SignalingClient, type SignalMessage } from "./signaling";
import type { GameModeId, TeamId } from "../levels/types";

interface HostPeer {
  connection: RTCPeerConnection;
  channel: RTCDataChannel;
  player: NetworkPlayerState;
}

export interface CortexHostOptions {
  serverId: string;
  hostPeerId: string;
  hostName: string;
  mode: GameModeId;
  mapId: string;
  iceServers?: RTCIceServer[];
  onPlayersChange?: (players: NetworkPlayerState[]) => void;
  onMessage?: (message: NetworkMessage, playerId: string) => void;
  onError?: (message: string) => void;
  onMatchStart?: () => void;
}

export class CortexWebRtcHost {
  private signaling: SignalingClient | null = null;
  private peers = new Map<string, HostPeer>();
  private started = false;

  constructor(private options: CortexHostOptions) {}

  async start() {
    if (this.started) return;
    this.started = true;
    this.signaling = createSignalingClient({
      serverId: this.options.serverId,
      peerId: this.options.hostPeerId,
      role: "host",
      playerName: this.options.hostName,
      onSignal: (message) => {
        void this.handleSignal(message);
      },
    });
    this.emitPlayers();
  }

  getPlayers(): NetworkPlayerState[] {
    const hostPlayer: NetworkPlayerState = {
      id: this.options.hostPeerId,
      name: this.options.hostName,
      team: "alpha",
      ready: true,
    };

    return [hostPlayer, ...Array.from(this.peers.values()).map((peer) => peer.player)];
  }

  private emitPlayers() {
    const players = this.getPlayers();
    this.options.onPlayersChange?.(players);
    this.broadcast({
      t: "c",
      c: "player-list",
      payload: { players },
    });
  }

  private async handleSignal(message: SignalMessage) {
    if (message.kind === "join") {
      const playerName = String(message.payload.playerName ?? "Pilot");
      const team = (message.payload.team as TeamId | undefined) ?? "bravo";
      await this.createPeer(String(message.from), playerName, team);
      return;
    }

    if (message.kind === "answer") {
      const peer = this.peers.get(message.from);
      if (!peer) return;
      await peer.connection.setRemoteDescription(message.payload.sdp as RTCSessionDescriptionInit);
      return;
    }

    if (message.kind === "ice") {
      const peer = this.peers.get(message.from);
      if (!peer) return;
      const candidate = message.payload.candidate as RTCIceCandidateInit | null;
      if (!candidate) return;
      await peer.connection.addIceCandidate(candidate);
    }
  }

  private async createPeer(peerId: string, playerName: string, team: TeamId) {
    if (!this.signaling || this.peers.has(peerId)) return;

    const connection = new RTCPeerConnection({ iceServers: this.options.iceServers });
    const channel = connection.createDataChannel("game", { ordered: true });
    const player: NetworkPlayerState = { id: peerId, name: playerName, team, ready: false };

    channel.onopen = () => {
      player.ready = true;
      this.emitPlayers();
      const joinedMessage: ControlMessage = {
        t: "c",
        c: "joined",
        payload: {
          serverId: this.options.serverId,
          mode: this.options.mode,
          mapId: this.options.mapId,
        },
      };
      channel.send(encodeMessage(joinedMessage));
    };

    channel.onclose = () => {
      this.removePeer(peerId);
    };

    channel.onmessage = (event) => {
      try {
        const message = decodeMessage(String(event.data));
        this.options.onMessage?.(message, peerId);
      } catch (error) {
        this.options.onError?.(error instanceof Error ? error.message : "Failed to decode peer message.");
      }
    };

    connection.onicecandidate = (event) => {
      if (!this.signaling) return;
      void this.signaling.send({
        kind: "ice",
        to: peerId,
        payload: { candidate: event.candidate?.toJSON() ?? null },
      });
    };

    connection.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(connection.connectionState)) {
        this.removePeer(peerId);
      }
    };

    this.peers.set(peerId, { connection, channel, player });

    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    await this.signaling.send({
      kind: "offer",
      to: peerId,
      payload: { sdp: offer },
    });
  }

  private removePeer(peerId: string) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.channel.close();
    peer.connection.close();
    this.peers.delete(peerId);
    this.emitPlayers();
  }

  broadcast(message: NetworkMessage) {
    const encoded = encodeMessage(message);
    for (const peer of this.peers.values()) {
      if (peer.channel.readyState === "open") {
        peer.channel.send(encoded);
      }
    }
  }

  startMatch() {
    this.broadcast({
      t: "c",
      c: "start-match",
      payload: {
        serverId: this.options.serverId,
        mode: this.options.mode,
        mapId: this.options.mapId,
      },
    });
    this.options.onMatchStart?.();
  }

  destroy() {
    this.broadcast({ t: "c", c: "host-quit", payload: {} });
    for (const peerId of Array.from(this.peers.keys())) {
      this.removePeer(peerId);
    }
    this.signaling?.close();
    this.signaling = null;
    this.started = false;
  }
}